import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useAppDispatch, useAppSelector } from "@/app/store/hooks";
import { setUser, updateTokens, logout as logoutAction } from "@/app/store/slices/authSlice";
import type { User } from "@/services/api/types";
import { getUserRole } from "@/utils/auth";
import { JMS_KEYS, clearLegacyAuthKeys } from "@/config/jmsStorage";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  loading: boolean;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const tokens = useAppSelector((state) => state.auth.tokens);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clearLegacyAuthKeys();

    const savedUser = localStorage.getItem(JMS_KEYS.user);
    const savedAccessToken = localStorage.getItem(JMS_KEYS.accessToken);
    const savedRefreshToken = localStorage.getItem(JMS_KEYS.refreshToken);
    
    if (savedUser && savedAccessToken) {
      const userData: User = JSON.parse(savedUser);
      
      if (!userData.role && savedAccessToken) {
        const role = getUserRole(savedAccessToken);
        if (role) {
          userData.role = role;
        }
      }
      
      dispatch(setUser(userData));
      
      if (savedAccessToken && savedRefreshToken) {
        dispatch(updateTokens({ accessToken: savedAccessToken, refreshToken: savedRefreshToken }));
      }
    }
    setLoading(false);
  }, [dispatch]);

  const login = (userData: User, accessToken: string, refreshToken: string) => {
    clearLegacyAuthKeys();
    dispatch(setUser(userData));
    dispatch(updateTokens({ accessToken, refreshToken }));
    localStorage.setItem(JMS_KEYS.user, JSON.stringify(userData));
    localStorage.setItem(JMS_KEYS.accessToken, accessToken);
    localStorage.setItem(JMS_KEYS.refreshToken, refreshToken);
  };

  const logout = () => {
    dispatch(logoutAction());
    localStorage.removeItem(JMS_KEYS.user);
    localStorage.removeItem(JMS_KEYS.accessToken);
    localStorage.removeItem(JMS_KEYS.refreshToken);
  };

  const getAccessToken = () => {
    return tokens.accessToken;
  };

  const value = {
    user,
    isAuthenticated: !!user && !!tokens.accessToken,
    login,
    logout,
    loading,
    getAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
